import type { Editor } from '@tiptap/core'
import * as styles from './styles'

/**
 * How a mention is stored inside the comment text. It mirrors legacy annotation
 * comments and, more importantly, `Wtm::Constants::MENTIONS_PATTERN` — the
 * pattern the notification fan-out scans the stored comment with to decide who
 * to notify. The read pattern stays as permissive about the resource type as the
 * server's; the composer only ever writes `user`.
 */
const MENTION_MARKUP = /@\[([^\]]+)\]\((\w+):([\w-]+)\)/g

/**
 * Flattens the composer's document to the plain comment text the API stores,
 * writing every mention node back as mention markup.
 *
 * The shared mention list packs `<id>:<resource type>:<document resource id>:
 * <document resource type>` into the node's id — tiptap gives it nowhere else to
 * put them — so only the leading segment is the user id. The round-trip is
 * covered by AnnotationOverlay's "writes a picked mention" test, which is what
 * would catch that format changing under us.
 */
export const serializeMentionText = (editor: Editor): string => {
  const paragraphs: string[] = []

  editor.state.doc.forEach((paragraph) => {
    let text = ''

    paragraph.forEach((node) => {
      const [userId] = String(node.attrs.id).split(':')
      text +=
        node.type.name === 'mention'
          ? `@[${node.attrs.label}](user:${userId})`
          : node.textContent
    })

    paragraphs.push(text)
  })

  return paragraphs.join('\n')
}

/**
 * Renders a stored comment, showing each mention as the name it was made
 * against. Only the reader sees names — the wire format stays the raw markup the
 * server needs.
 */
export const MentionText: React.VFC<{ text: string }> = ({ text }) => {
  const parts: React.ReactNode[] = []
  let cursor = 0

  for (const match of text.matchAll(MENTION_MARKUP)) {
    const [markup, name] = match
    const start = match.index ?? 0

    if (start > cursor) parts.push(text.slice(cursor, start))
    parts.push(
      <span key={start} css={styles.mentionName}>
        @{name}
      </span>
    )
    cursor = start + markup.length
  }

  parts.push(text.slice(cursor))

  return <>{parts}</>
}
